/**
 * §I-2.5 · Neutral, opt-in prompt to recover anonymous analyses created
 * on this browser. Shows NO details (no count, address, type, date or
 * value) before the owner confirms. Linking happens only on confirm.
 */
import React from "react";
import { Loader2 } from "lucide-react";
import {
  checkAnonLinkAvailable,
  confirmAnonLink,
  dismissAnonLink,
  anonLinkDismissed,
} from "@/lib/vg2AnonLink";

export default function AnonLinkPrompt({ onLinked }) {
  const [show, setShow] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (anonLinkDismissed()) return;
      const available = await checkAnonLinkAvailable();
      if (!cancelled && available) setShow(true);
    })();
    return () => { cancelled = true; };
  }, []);

  if (!show) return null;

  const accept = async () => {
    setBusy(true); setErr(null);
    try {
      await confirmAnonLink();
      setShow(false);
      if (onLinked) onLinked();
    } catch (e) {
      setErr("Non è stato possibile associare le analisi. Riprova.");
      setBusy(false);
    }
  };

  const refuse = () => {
    dismissAnonLink();
    setShow(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
         data-testid="anon-link-prompt">
      <div className="w-full max-w-md rounded-2xl bg-white border border-gray-200 p-7 shadow-xl">
        <h3 className="text-lg font-bold text-[#0A0A0A]"
            data-testid="anon-link-prompt-title">
          Analisi salvate su questo browser
        </h3>
        <p className="text-sm text-gray-600 mt-2" data-testid="anon-link-prompt-body">
          Sono presenti analisi salvate precedentemente su questo browser.
          Vuoi associarle al tuo account? Fallo solo se le hai fatte tu.
        </p>
        {err && (
          <p className="text-sm text-[#B40101] mt-3" data-testid="anon-link-prompt-error">{err}</p>
        )}
        <div className="flex items-center justify-end gap-3 mt-6">
          <button onClick={refuse} disabled={busy}
                  className="text-sm text-gray-500 hover:text-[#0A0A0A] px-4 py-2"
                  data-testid="anon-link-refuse">
            No, grazie
          </button>
          <button onClick={accept} disabled={busy}
                  className="vg-btn-primary flex items-center gap-2 disabled:opacity-60"
                  data-testid="anon-link-confirm">
            {busy && <Loader2 className="animate-spin" size={16} />}
            Associa al mio account
          </button>
        </div>
      </div>
    </div>
  );
}
